import React, { useContext, useEffect, createRef, useRef } from "react";
import { detectDeviceType } from "../../utils/detectDeviceType";
import { Store } from "../../context/Store";
import {
  Headline,
  Plaintext,
  CharacterSelection,
  ImgCtaButtons,
  BulletList,
  Img,
  ImgDesc,
  Tips,
  Slider,
  PersonaTranscript,
  UserTranscript,
} from "../index";
import {
  PersistantContentContainer,
  FadeOutOverlay,
  TranscriptBox,
} from "./PersistantContentStyles";
import {
  HeadlineTypes,
  PlaintextTypes,
  CharacterSelectionTypes,
  ImgCtaButtonsTypes,
  BulletListTypes,
  TipsTypes,
  SliderTypes,
  ImgTypes,
  ImgDescTypes,
  PersonaTranscriptTypes,
  UserTranscriptTypes,
  RedirectTypes,
  SurveryTypes,
} from "../../types/ContentCard";
import { Persona } from "@soulmachines/smwebsdk";

const PersistantContent: React.FC = () => {
  const { state, dispatch } = useContext(Store);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = createRef<HTMLDivElement>();
  const { transcript, tabletLandscape, persona, conversationTranscript } =
    state;

  // User has switched back to the tab
  const onFocus = () => {
    setTimeout(() => {
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 1000);
  };

  useEffect(() => {
    window.addEventListener("focus", onFocus);

    // Specify how to clean up after this effect:
    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 1000);
  }, [transcript, conversationTranscript]);

  const renderContentCard = (
    contentCardData:
      | HeadlineTypes
      | PlaintextTypes
      | CharacterSelectionTypes
      | BulletListTypes
      | TipsTypes
      | SliderTypes
      | ImgTypes
      | ImgDescTypes
      | PersonaTranscriptTypes
      | UserTranscriptTypes
      | RedirectTypes
      | SurveryTypes
      | ImgCtaButtonsTypes,

    index: number,
    active: boolean
  ) => {
    if (!contentCardData) return null;

    const components: { [key: string]: JSX.Element } = {
      headline: (
        <Headline
          contentCardData={contentCardData as HeadlineTypes}
          key={contentCardData.component + index}
          tabletLandscape={tabletLandscape}
        />
      ),
      plaintext: (
        <Plaintext
          contentCardData={contentCardData as PlaintextTypes}
          key={contentCardData.component + index}
        />
      ),
      characters: (
        <CharacterSelection
          contentCardData={contentCardData as CharacterSelectionTypes}
          key={contentCardData.component + index}
          persona={persona as Persona}
          tabletLandscape={tabletLandscape}
          dispatch={dispatch}
          active={active}
        />
      ),
      imgCtaButtons: (
        <ImgCtaButtons
          contentCardData={contentCardData as ImgCtaButtonsTypes}
          key={contentCardData.component + index}
          persona={persona as Persona}
          tabletLandscape={tabletLandscape}
          dispatch={dispatch}
          active={active}
        />
      ),
      bulletList: (
        <BulletList
          contentCardData={contentCardData as BulletListTypes}
          key={contentCardData.component + index}
          tabletLandscape={tabletLandscape}
        />
      ),
      img: (
        <Img
          contentCardData={contentCardData as ImgTypes}
          key={contentCardData.component + index}
          tabletLandscape={tabletLandscape}
        />
      ),
      imgDesc: (
        <ImgDesc
          contentCardData={contentCardData as ImgDescTypes}
          key={contentCardData.component + index}
          tabletLandscape={tabletLandscape}
        />
      ),
      tips: (
        <Tips
          contentCardData={contentCardData as TipsTypes}
          key={contentCardData.component + index}
          tabletLandscape={tabletLandscape}
          persona={persona as Persona}
          dispatch={dispatch}
          active={active}
        />
      ),
      slider: (
        <Slider
          contentCardData={contentCardData as SliderTypes}
          key={contentCardData.component + index}
          tabletLandscape={tabletLandscape}
          persona={persona as Persona}
          dispatch={dispatch}
          active={active}
        />
      ),
      personaTranscript: (
        <PersonaTranscript
          contentCardData={contentCardData as PersonaTranscriptTypes}
          key={contentCardData.component + index}
          index={index}
        />
      ),
      userTranscript: (
        <UserTranscript
          contentCardData={contentCardData as UserTranscriptTypes}
          key={contentCardData.component + index}
          tabletLandscape={tabletLandscape}
        />
      ),
    };

    return components[contentCardData.component];
  };

  return (
    <PersistantContentContainer
      deviceType={detectDeviceType()}
      isLandscape={tabletLandscape}
      onScroll={() => {
        if (scrollRef.current) {
          scrollRef.current.style.opacity = "1";
          scrollRef.current.style.transition = "opacity 100ms ease-out 0s";
          setTimeout(() => {
            if (scrollRef.current) {
              scrollRef.current.style.opacity = "0";
            }
          }, 1000);
        }
      }}
    >
      <TranscriptBox
        deviceType={detectDeviceType()}
        isLandscape={tabletLandscape}
      >
        {/* Fade out effect */}
        <FadeOutOverlay ref={scrollRef} />
        {transcript.map((contentCard, index) => {
          if (
            contentCard.content &&
            contentCard.content.component.includes("Transcript") &&
            !conversationTranscript
          )
            return null;
          return renderContentCard(
            contentCard.content,
            index,
            // This is how we decided which button we leave active
            transcript.length - 1 === index ? true : false
          );
        })}
        {/* This Div is here in order to scroll to bottom with new Transcript Message */}
        <div ref={bottomRef} />
      </TranscriptBox>
    </PersistantContentContainer>
  );
};

export default PersistantContent;
